export type Diff = Record<string, { old: any; new: any }>;

export type BranchRef = { id: number; name: string; location: string };
export type PartnerRef = { id: number; name: string };

export const ERVARING_LABELS: Record<string, string> = {
  "0 years":   "Geen ervaring",
  "1-3 years": "1-3 jaar",
  "3-5 years": "3-5 jaar",
  "5+ years":  "5+ jaar",
};

const STATUS_NL: Record<string, string> = { open: "open", closed: "gesloten", on_hold: "on hold" };
const BEMIDDELING_NL: Record<string, string> = {
  geen: "geen", in_bemiddeling: "bemiddeling", shortlisted: "shortlist", geplaatst: "geplaatst",
};
const SALARY_TYPE_NL: Record<string, string> = { range: "Bandbreedte", fixed: "Vast bedrag", other: "Anders" };

const FIELD_LABELS: Record<string, string> = {
  role: "Functie titel",
  location: "Locatie",
  experience: "Ervaring",
  number_of_positions: "Aantal posities",
  contactpersoon: "Contactpersoon",
  branch_id: "Vestiging",
  partner_id: "Partner",
  priority: "Prioriteit",
  status: "Status",
  bemiddeling_status: "Bemiddeling",
  comments: "Opmerkingen",
  reviewed_at: "Bevestigd",
  disciplines: "Discipline",
  hiring_manager: "Hiring manager",
  salary_type: "Salarisindicatie",
  salary_min: "Salaris min",
  salary_max: "Salaris max",
  salary_fixed: "Salaris vast",
  salary_other: "Salaris anders",
};

const SNAPSHOT_HIDDEN_FIELDS = new Set(["id", "created_at", "updated_at", "created_by", "updated_by"]);

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

function humanizeKey(key: string): string {
  return key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function fieldLabel(key: string, modelName?: string): string {
  if (modelName === "job_openings" && FIELD_LABELS[key]) return FIELD_LABELS[key];
  return humanizeKey(key);
}

export function activityLabel(action: string, payload: Record<string, any> | null, modelName?: string): string {
  if (modelName === "job_openings") {
    switch (action) {
      case "create":       return "Vacature aangemaakt";
      case "confirmed":    return "Bevestigd actueel";
      case "status_change": {
        const from = STATUS_NL[payload?.old_status ?? ""] ?? payload?.old_status ?? "?";
        const to   = STATUS_NL[payload?.new_status ?? ""] ?? payload?.new_status ?? "?";
        return `Status: ${from} → ${to}`;
      }
      case "bemiddeling_change": {
        const from = BEMIDDELING_NL[payload?.old_bemiddeling_status ?? ""] ?? payload?.old_bemiddeling_status ?? "?";
        const to   = BEMIDDELING_NL[payload?.new_bemiddeling_status ?? ""] ?? payload?.new_bemiddeling_status ?? "?";
        return `Bemiddeling: ${from} → ${to}`;
      }
      case "update": {
        // A status change has no log row of its own: an update whose only
        // real change is the status is shown as one.
        const status = payload?.status;
        if (status && isStatusOnlyUpdate(payload)) {
          const from = STATUS_NL[status.old ?? ""] ?? status.old ?? "?";
          const to   = STATUS_NL[status.new ?? ""] ?? status.new ?? "?";
          return `Status: ${from} → ${to}`;
        }
        return "Gewijzigd";
      }
      case "delete":  return "Verwijderd";
      default:        return action;
    }
  }
  switch (action) {
    case "create": return "Aangemaakt";
    case "update": return "Gewijzigd";
    case "delete": return "Verwijderd";
    case "status_change": {
      const from = payload?.old_status ?? "?";
      const to   = payload?.new_status ?? "?";
      return `Status: ${from} → ${to}`;
    }
    case "bemiddeling_change": {
      const from = payload?.old_bemiddeling_status ?? "?";
      const to   = payload?.new_bemiddeling_status ?? "?";
      return `Bemiddeling: ${from} → ${to}`;
    }
    default: return action;
  }
}

// isReviewedAtOnlyUpdate reports whether an "update" log only touched
// reviewed_at. "Bevestig actueel" writes both that generic update (via the
// GORM audit callback) and a dedicated "confirmed" entry, so the update is
// redundant in a per-vacature timeline. Mirrors the backend's
// isReviewedAtOnlyChange (internal/worker/activity_poller.go).
export function isReviewedAtOnlyUpdate(action: string, payload: Record<string, any> | null): boolean {
  if (action !== "update" || !payload) return false;
  const cols = Object.keys(payload);
  return cols.length > 0 && cols.every(c => c === "reviewed_at");
}

// The update diff holds every column the request sent, changed or not, and
// stores the old disciplines value as a Postgres array literal ("{a,b}")
// next to a JSON list as the new one. Mirrors the backend's sameRawValue
// (internal/worker/vacature_changes.go).
function normalizeValue(key: string, v: any): string {
  if (v === null || v === undefined || v === "") return "";
  if (key === "disciplines" && typeof v === "string" && v.startsWith("{") && v.endsWith("}")) {
    const inner = v.slice(1, -1);
    v = inner === "" ? [] : inner.split(",").map(p => p.replace(/^"(.*)"$/, "$1"));
  }
  if (Array.isArray(v)) return v.length === 0 ? "" : JSON.stringify(v.map(String));
  return JSON.stringify(v);
}

export function isFieldChanged(key: string, entry: { old: any; new: any }): boolean {
  return normalizeValue(key, entry.old) !== normalizeValue(key, entry.new);
}

// changedFields lists the columns of an "update" diff that actually changed.
// reviewed_at is left out: any real change bumps it as a side effect.
export function changedFields(payload: Record<string, any> | null): string[] {
  if (!payload) return [];
  return Object.entries(payload as Diff)
    .filter(([key, entry]) => key !== "reviewed_at" && entry && typeof entry === "object" && isFieldChanged(key, entry))
    .map(([key]) => key);
}

function isStatusOnlyUpdate(payload: Record<string, any> | null): boolean {
  const changed = changedFields(payload);
  return changed.length === 1 && changed[0] === "status";
}

export function normalizeDiff(action: string, payload: Record<string, any> | null): Diff | null {
  if (!payload) return null;
  if (action === "update") return payload as Diff;
  if (action === "status_change") return { status: { old: payload.old_status, new: payload.new_status } };
  if (action === "bemiddeling_change") {
    return { bemiddeling_status: { old: payload.old_bemiddeling_status, new: payload.new_bemiddeling_status } };
  }
  return null;
}

export function snapshotEntries(payload: Record<string, any> | null): Array<[string, any]> {
  if (!payload) return [];
  return Object.entries(payload).filter(([k]) => !SNAPSHOT_HIDDEN_FIELDS.has(k));
}

export function formatFieldValue(
  key: string,
  value: any,
  modelName?: string,
  branches?: BranchRef[],
  partners?: PartnerRef[]
): string {
  if (value === null || value === undefined || value === "") return "—";
  if (modelName === "job_openings") {
    if (key === "experience") return ERVARING_LABELS[value] ?? String(value);
    if (key === "status") return STATUS_NL[value] ?? String(value);
    if (key === "bemiddeling_status") return BEMIDDELING_NL[value] ?? String(value);
    if (key === "branch_id") {
      const branch = branches?.find(b => b.id === Number(value));
      return branch ? `${branch.name} — ${branch.location}` : `#${value}`;
    }
    if (key === "partner_id") {
      const partner = partners?.find(p => p.id === Number(value));
      return partner ? partner.name : `#${value}`;
    }
    if (key === "salary_type") return SALARY_TYPE_NL[value] ?? String(value);
    if (key === "salary_min" || key === "salary_max" || key === "salary_fixed") {
      return `€${Number(value).toLocaleString("nl-NL")}`;
    }
  }
  if (key.endsWith("_at")) return fmtDate(value);
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nee";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
